import { EventEmitter } from "eventemitter3";
import { HeliusSource } from "../sources/helius.js";
import { XSource } from "../sources/x.js";
import { cadenceDetector } from "../detectors/cadence.js";
import { onchainDetector } from "../detectors/onchain.js";
import { voiceDetector } from "../detectors/voice.js";
import { timingDetector } from "../detectors/timing.js";
import { correlationDetector } from "../detectors/correlation.js";
import { aggregate } from "./aggregator.js";
import { ScanError } from "./errors.js";
import type {
  PulseConfig,
  ScanInput,
  ScanOptions,
  Verdict,
  DetectorResult,
  DetectorName,
} from "./types.js";

/**
 * The main entry point for Pulse Protocol.
 *
 * @example
 * ```ts
 * const pulse = new PulseProtocol({
 *   heliusApiKey: process.env.HELIUS_API_KEY!,
 *   xApiKey: process.env.X_API_KEY!,
 * });
 *
 * const verdict = await pulse.scan({
 *   handle: "some_agent",
 *   wallet: "6ySH9oi...",
 * });
 * ```
 */
export class PulseProtocol {
  readonly events: EventEmitter;

  private readonly helius: HeliusSource;
  private readonly x: XSource;
  private readonly config: Required<Omit<PulseConfig, "signingKey" | "rpcUrl" | "feedUrl">> &
    Pick<PulseConfig, "signingKey" | "rpcUrl" | "feedUrl">;

  constructor(config: PulseConfig) {
    if (!config.heliusApiKey) {
      throw new Error("heliusApiKey is required. Get one at https://helius.xyz");
    }
    if (!config.xApiKey) {
      throw new Error("xApiKey is required.");
    }

    this.config = {
      heliusApiKey: config.heliusApiKey,
      xApiKey: config.xApiKey,
      rpcUrl: config.rpcUrl,
      feedUrl: config.feedUrl,
      signingKey: config.signingKey,
      timeout: config.timeout ?? 30_000,
      maxRetries: config.maxRetries ?? 2,
    };

    this.helius = new HeliusSource({
      apiKey: config.heliusApiKey,
      rpcUrl: config.rpcUrl,
      timeout: this.config.timeout,
      maxRetries: this.config.maxRetries,
    });

    this.x = new XSource({
      apiKey: config.xApiKey,
      timeout: this.config.timeout,
      maxRetries: this.config.maxRetries,
    });

    this.events = new EventEmitter();
  }

  /**
   * Run a full scan on a target and return a verdict.
   *
   * This fetches X posts, on-chain history, cross-correlates them, runs all
   * five detectors, and aggregates the result. A typical scan completes in
   * 2-8 seconds depending on source latency.
   */
  async scan(input: ScanInput, options: ScanOptions = {}): Promise<Verdict> {
    this.events.emit("scan:start", input);

    const [posts, txs] = await Promise.all([
      this.x.getRecentPosts(input.handle, { limit: 200 }),
      this.helius.getWalletHistory(input.wallet, { limit: 500 }),
    ]);

    if (posts.length === 0 && txs.length === 0) {
      throw new ScanError(
        `No data found for @${input.handle} or wallet ${input.wallet.slice(0, 8)}...`
      );
    }

    const requested: DetectorName[] =
      options.only ?? ["cadence", "onchain", "voice", "timing", "correlation"];

    const results: DetectorResult[] = [];

    for (const name of requested) {
      const started = Date.now();
      try {
        let result: DetectorResult;
        switch (name) {
          case "cadence":
            result = cadenceDetector(posts);
            break;
          case "onchain":
            result = onchainDetector(txs);
            break;
          case "voice":
            result = voiceDetector(posts);
            break;
          case "timing":
            result = timingDetector(posts, txs);
            break;
          case "correlation":
            result = correlationDetector(posts, txs, input);
            break;
        }
        result.runtime_ms = Date.now() - started;
        results.push(result);
        this.events.emit("detector:done", result);
      } catch (err) {
        throw new ScanError(
          `Detector ${name} failed: ${(err as Error).message}`,
          name
        );
      }
    }

    const verdict = aggregate(input, results, options);
    this.events.emit("scan:done", verdict);
    return verdict;
  }

  /**
   * Scan a batch of targets concurrently. Respects the configured rate limits
   * on underlying sources. Use for watchlists.
   */
  async scanMany(inputs: ScanInput[], options: ScanOptions = {}): Promise<Verdict[]> {
    const results: Verdict[] = [];
    for (const input of inputs) {
      try {
        results.push(await this.scan(input, options));
      } catch (err) {
        this.events.emit("scan:error", { input, error: err });
      }
    }
    return results;
  }
}
